import React from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { NotificationBell } from '../NotificationBell';
import { Avatar } from '../ui/Avatar';
import { OrgSwitcher } from './OrgSwitcher';
import { useCurrentUser } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Sun, Moon, Monitor, X } from 'lucide-react';

export const Navbar = () => {
  const { node, isGlobal, brand, isLoading: isTenantLoading, userThemePref, setThemePref, clearActiveOrg } = useTenant();
  const { user, memberships = [], isLoading: isUserLoading } = useCurrentUser();
  const { isSuperAdmin, isSystemAdmin } = usePermissions();
  
  const userInitials = React.useMemo(() => {
    if (!user) return 'GS';
    if (user.firstName) return `${user.firstName[0]}${user.lastName?.[0] || ''}`;
    const nameStr = user.fullName || user.name || '';
    if (nameStr) {
      const parts = nameStr.trim().split(/\s+/);
      return parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : parts[0].slice(0, 2);
    }
    if (user.email) return user.email.slice(0, 2).toUpperCase();
    return 'ME';
  }, [user]);

  const isLoading = isTenantLoading || isUserLoading;

  if (isLoading) {
    return (
      <nav 
        className="flex items-center h-20 px-6 bg-white dark:bg-black sticky top-0 z-50 border-b-8 border-black dark:border-white shadow-[0px_4px_0px_#000000] dark:shadow-[0px_4px_0px_#FFFFFF]"
      >
        <Link to="/" className="hover:opacity-80 transition-opacity flex items-center gap-3">
          {brand.logoUrl ? (
            <img 
              src={brand.logoUrl} 
              alt={brand.name} 
              className="h-8 w-8 object-contain bg-slate-900/5 dark:bg-white/90 p-0.5 rounded-sm" 
              referrerPolicy="no-referrer" 
            />
          ) : (
            <div className="w-8 h-8 border-2 border-black dark:border-white flex items-center justify-center font-black text-lg bg-[#FFD600] text-black">
              {brand.name ? brand.name[0] : 'O'}
            </div>
          )}
          <span className="font-display font-black text-2xl md:text-3xl tracking-tighter uppercase text-black dark:text-white hidden sm:inline">
            {brand.name}
          </span>
        </Link>
        <div className="flex-1" />
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-none bg-black/10 dark:bg-white/10 animate-pulse" />
          <div className="w-8 h-8 rounded-none bg-black/10 dark:bg-white/10 animate-pulse" />
        </div>
      </nav>
    );
  }

  return (
    <nav 
      className="flex items-center h-20 px-6 bg-white dark:bg-black sticky top-0 z-50 border-b-8 border-black dark:border-white shadow-[0px_4px_0px_#000000] dark:shadow-[0px_4px_0px_#FFFFFF]"
    >
      <Link to="/" className="hover:opacity-80 transition-opacity flex items-center gap-3">
        {brand.logoUrl ? (
          <img 
            src={brand.logoUrl} 
            alt={brand.name} 
            className="h-8 w-8 object-contain bg-slate-900/5 dark:bg-white/90 p-0.5 rounded-sm" 
            referrerPolicy="no-referrer" 
          />
        ) : (
          <div className="w-8 h-8 border-2 border-black dark:border-white flex items-center justify-center font-black text-lg bg-[#FFD600] text-black">
            {brand.name ? brand.name[0] : 'O'}
          </div>
        )}
        <span className="font-display font-black text-2xl md:text-3xl tracking-tighter uppercase text-black dark:text-white hidden sm:inline">
          {brand.name}
        </span>
      </Link>

      {!isGlobal && node && (
        <>
          <span className="text-black/30 dark:text-white/30 font-bold mx-2 font-mono">/</span>
          <div className="flex items-center gap-2">
            <span className="text-xs font-black uppercase tracking-widest font-mono text-black dark:text-white bg-black/5 dark:bg-white/10 px-2 py-1 border border-black/20 dark:border-white/20 truncate max-w-[140px] sm:max-w-xs">
              {node.name}
            </span>
            {node.tier && (
              <span className="text-[10px] font-black uppercase tracking-widest font-mono text-black dark:text-white border-2 border-black dark:border-white bg-[#0044FF] text-white px-1.5 py-0.5 shadow-[1px_1px_0px_#000000]">
                {node.tier}
              </span>
            )}
            <button
              onClick={() => clearActiveOrg()}
              className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider font-mono bg-white dark:bg-slate-900 text-black dark:text-white border-2 border-black dark:border-white px-2 py-1 hover:bg-[#FFD600] hover:text-black transition-colors shadow-[1px_1px_0px_#000000] dark:shadow-[1px_1px_0px_#FFFFFF] cursor-pointer ml-1"
              title="Exit org view (Return to Platform Root)"
            >
              <X size={12} className="stroke-[3]" />
              <span className="hidden sm:inline">Exit Org</span>
            </button>
          </div>
        </>
      )}
      
      <div className="flex-1" />
      
      <div className="flex items-center gap-4">
        {user && (
          <>
            <NotificationBell className="w-10 h-10 border-2 border-black bg-white flex items-center justify-center text-black hover:bg-[#FFD600] hover:text-black rounded-none p-0 transition-colors shadow-[2px_2px_0px_#000000] dark:bg-slate-900 dark:text-white" />
            {(memberships.length >= 1 || isSuperAdmin || isSystemAdmin || !isGlobal) && <OrgSwitcher />}
          </>
        )}

        <button
          onClick={() => {
            if (userThemePref === 'light') setThemePref('dark');
            else if (userThemePref === 'dark') setThemePref('system');
            else setThemePref('light');
          }}
          className="w-10 h-10 border-2 border-black bg-white flex items-center justify-center text-black hover:bg-[#FFD600] hover:text-black rounded-none p-0 transition-colors shadow-[2px_2px_0px_#000000] dark:bg-slate-900 dark:text-white"
          title={`Theme: ${userThemePref}`}
        >
          {userThemePref === 'light' ? (
            <Sun size={20} />
          ) : userThemePref === 'dark' ? (
            <Moon size={20} />
          ) : (
            <Monitor size={20} />
          )}
        </button>

        <Link 
          to={user ? "/profile" : "/sign-in"} 
          className="hover:-translate-y-1 transition-transform border-2 border-black shadow-[2px_2px_0px_#000000] bg-white inline-block" 
          title="My Passport & Profile"
          aria-label="My Passport & Profile"
        >
          <Avatar 
            userId={user?.id || 'guest'} 
            initials={userInitials} 
            imageUrl={user?.imageUrl || user?.avatar}
            size="sm" 
            className="rounded-none w-10 h-10 text-black font-black uppercase"
          />
          <span className="sr-only">My Passport & Profile</span>
        </Link>
      </div>
    </nav>
  );
};
